import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Boxes, RefreshCw, ShieldCheck, Zap, Check } from 'lucide-react';
import { Container, PageHeader, SplitView, Breadcrumbs, BreadcrumbItem } from '@vnetwork/ui/layout';
import {
  Badge,
  Button,
  IconButton,
  Input,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  CardFooter,
} from '@vnetwork/ui/primitives';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import { Field } from '@vnetwork/ui/forms';
import { useCreateCluster, useK8sConfig, k8sNetworkCidr } from '@/api/k8s';
import {
  defaultDraft,
  buildAddons,
  ADDON_CATALOG,
  type ClusterDraft,
  type PoolDraft,
} from './cluster-create/cluster-draft';
import { PoolsStep } from './cluster-create/PoolsStep';
import { AddonsStep } from './cluster-create/AddonsStep';
import { TagsStep } from './cluster-create/TagsStep';

const FALLBACK_VERSIONS = ['v1.33.1', 'v1.32.0', 'v1.31.0'];

// Cluster-name rules mirror @console: 10–100 chars, must start with a letter or
// number, then letters / numbers / hyphen / underscore.
const CLUSTER_NAME_RE = /^[a-zA-Z0-9][a-zA-Z0-9_-]{9,99}$/;

export function ClusterCreate() {
  const navigate = useNavigate();
  const { serviceId } = useParams<{ serviceId: string }>();
  const { show } = useToast();
  const base = `/services/kubernetes/${serviceId}/clusters`;
  const { data: config, refetch } = useK8sConfig(serviceId);

  const versions = config?.versions?.length ? config.versions : FALLBACK_VERSIONS;
  const runtimes = config?.runtimes ?? [{ id: 'containerd', name: 'containerd' }];
  const cnis = config?.cnis ?? [{ value: 'calico', label: 'Calico CNI' }];
  const flavors = config?.flavors ?? [];
  const gpuModels = config?.gpuModels ?? [];
  const resourceGroups = config?.resourceGroups ?? [];
  const networks = config?.networks ?? [];

  const [d, setD] = useState<ClusterDraft>(defaultDraft);
  const patch = (p: Partial<ClusterDraft>) => setD((s) => ({ ...s, ...p }));

  // Cluster tier — chosen per cluster under the one Managed Kubernetes service.
  // Enterprise runs on the legacy control plane (available now); the Developer
  // (hosted control plane) tier is temporarily disabled until provisioning ships.
  const [tier, setTier] = useState<ClusterTier>('enterprise');

  // Seed both the system uid and the (editable) cluster name from the server-suggested
  // slug (k8s-c-<randomly>), matching @console. Re-seeds whenever `randomly` changes —
  // each config refetch (the Refresh button) returns a fresh slug, so both regenerate
  // together (legacy resets name + uid in lockstep). uid stays system-managed; the name
  // is pre-filled but the user can edit it afterwards.
  useEffect(() => {
    if (config?.randomly) setD((s) => ({ ...s, uid: `k8s-c-${config.randomly}`, name: `k8s-c-${config.randomly}` }));
  }, [config?.randomly]);

  useEffect(() => {
    setD((s) => ({
      ...s,
      version: s.version || versions[0] || '',
      runtime: s.runtime || runtimes[0]?.id || 'containerd',
      network: {
        ...s.network,
        cni: s.network.cni || cnis[0]?.value || 'calico',
        subnet: s.network.subnet || networks[0]?.id || '',
      },
      resource_group: s.resource_group || resourceGroups[0]?.id || resourceGroups[0]?.value || '',
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versions.length, runtimes.length, cnis.length, resourceGroups.length, networks.length]);

  const create = useCreateCluster(serviceId!, {
    onSuccess: (res) => {
      show({
        tone: 'success',
        title: 'Cluster creation started',
        description: 'Provisioning has begun — track its progress on the cluster page.',
      });
      // Land on the new cluster's detail page so its provisioning banner can track
      // status live; fall back to the list if the id is missing from the response.
      const body = res as { data?: { cluster?: { id?: string } }; cluster?: { id?: string } };
      const id = body?.data?.cluster?.id ?? body?.cluster?.id;
      navigate(id ? `${base}/${id}` : base);
    },
    onError: (e) => show({ tone: 'danger', title: (e as Error)?.message || 'Create failed' }),
  });

  // Validation.
  const nameOk = CLUSTER_NAME_RE.test(d.name.trim());
  const poolsOk = d.pools.every((p) => p.flavor && p.min >= 1 && p.max >= p.min && (!p.enableGpu || p.gpu.type));
  // The backend requires a network for the cluster's subnet when any are offered.
  const subnetOk = networks.length === 0 || !!d.network.subnet;

  // Over-quota: a pool's max node count exceeds the slots available for its flavor.
  const flavorByName = (name: string) => flavors.find((f) => f.name === name);
  const overQuota = d.pools.some((p) => {
    const f = flavorByName(p.flavor);
    return f?.available != null && p.max > f.available;
  });
  const totalMaxNodes = d.pools.reduce((s, p) => s + (Number(p.max) || 0), 0);
  const enabledAddonDefs = ADDON_CATALOG.filter((def) => d.addons[def.key]?.enabled);
  const rg = resourceGroups.find((r) => String(r.id ?? r.value) === d.resource_group);
  const resourceGroupLabel = rg?.name ?? rg?.label ?? (d.resource_group || '—');
  const selectedNet = networks.find((n) => n.id === d.network.subnet);
  const subnetLabel = selectedNet
    ? `${selectedNet.name || selectedNet.id}${k8sNetworkCidr(selectedNet) ? ` (${k8sNetworkCidr(selectedNet)})` : ''}`
    : '—';

  const submit = () => {
    // The backend does not synthesize worker VMs from the flavor — it expects each pool
    // pre-expanded with cores/memory and a `machines` array holding one entry per `min`.
    // Omitting these makes the Ruby side call .map on a nil `machines` and 500. Resolve
    // cores/memory from the flavor record (authoritative — the backend nodepool path reads
    // flavor.cores/.memory too); fall back to parsing the `type-class-cores-memory` name.
    const pools = d.pools.map((p: PoolDraft) => {
      const f = flavorByName(p.flavor);
      const [, , cn, mn] = p.flavor.split('-');
      const cores = f?.cores ?? Number(cn);
      const memory = f?.memory ?? Number(mn);
      const gpu = p.enableGpu && p.gpu.type ? { type: p.gpu.type, count: Number(p.gpu.count) || 1 } : undefined;
      const machines = Array.from({ length: Number(p.min) || 0 }, (_, id) => ({
        name: `${d.uid}-${p.name}-worker-${id}`,
        image: 'Kubernetes Techev 25.05',
        type: 'kubernetes=worker',
        flavor: p.flavor,
        cores: Number(cores),
        memory: Number(memory),
        volumes: Number(p.volumes),
        ...(gpu ? { gpu } : {}),
      }));
      return {
        name: p.name,
        uid: d.uid,
        image: 'Kubernetes Techev 25.05',
        roles: 'worker',
        type: p.type || 'standard',
        flavor: p.flavor,
        cores: Number(cores),
        memory: Number(memory),
        min: Number(p.min),
        max: Number(p.max),
        volumes: Number(p.volumes),
        autoscaling: p.autoscaling,
        machines,
        ...(gpu ? { gpu } : {}),
      };
    });
    const tagsObj = Object.fromEntries(d.tags.filter((t) => t.key.trim()).map((t) => [t.key.trim(), t.value]));
    create.mutate({
      cluster: {
        // name = customer's personal label; uid = system identifier (k8s-c-<slug>, not
        // editable). The backend derives the cluster fqdn + worker names from uid, so it
        // is sent independently of the name.
        name: d.name.trim(), uid: d.uid, description: d.description, version: d.version, runtime: d.runtime,
        // Backend reads the resource-group id under `resource` (not `resource_group`).
        ...(d.resource_group ? { resource: d.resource_group } : {}),
        network: d.network, pools, addons: buildAddons(d.addons),
        ...(Object.keys(tagsObj).length ? { tags: tagsObj } : {}),
      },
    } as unknown as Record<string, unknown>);
  };

  const refetchAction = (
    <IconButton variant="ghost" size="sm" aria-label="Refresh configuration" onClick={() => refetch()}>
      <RefreshCw size={15} />
    </IconButton>
  );

  return (
    <Container size="lg">
      <PageHeader
        icon={<Boxes size={22} />}
        breadcrumbs={
          <Breadcrumbs>
            <BreadcrumbItem href={base} onClick={(e) => { e.preventDefault(); navigate(base); }}>Clusters</BreadcrumbItem>
            <BreadcrumbItem current>Create</BreadcrumbItem>
          </Breadcrumbs>
        }
        title="Kubernetes Clusters Create"
        description="Automating the creation of Kubernetes clusters, you can accelerate your development lifecycle and enjoy the benefits of containerized orchestration without the hassle."
      />

      <SplitView
        asideWidth="360px"
        aside={
          <Card style={{ position: 'sticky', top: 16 }}>
            <CardHeader>
              <CardTitle>Kubernetes Cluster Summary</CardTitle>
              <CardDescription>Validate your Kubernetes cluster configuration before you create it.</CardDescription>
            </CardHeader>
            <CardBody>
              {overQuota && (
                <Alert tone="danger" title="Exceed quota" style={{ marginBottom: 'var(--vn-space-3)' }}>
                  Your cluster configuration exceeds the slots available for a selected flavor. Lower the node count or
                  contact support.
                </Alert>
              )}

              <SumGroup first>
                <SumField label="Cluster Name" value={d.name || '—'} />
                <SumField label="Resource Group" value={resourceGroupLabel} />
              </SumGroup>

              <SumGroup>
                <SumField label="Kubernetes Configuration" value={<>{d.version || '—'}<br />{d.runtime || '—'}</>} />
                <SumField label="Network CNI" value={d.network.cni || '—'} />
                {networks.length > 0 && <SumField label="Network Subnet" value={subnetLabel} />}
                <SumField label="Network POD's CIDR — Service's CIDR" value={`${d.network.pod_cidr} — ${d.network.service_cidr}`} />
              </SumGroup>

              <SumGroup>
                <SumField
                  label="Managed Control Plane (Standard)"
                  value={<>High Available — Yes<br />Maximum Node Pool — 3<br />Maximum Node per Pool — 10</>}
                />
              </SumGroup>

              <SumGroup>
                <div style={{ fontSize: 'var(--vn-font-size-md)', fontWeight: 700, color: 'var(--vn-fg-muted)' }}>
                  Data Plane Pools (NodeGroup x {d.pools.length})
                </div>
                <p style={{ margin: '4px 0 0', fontSize: 'var(--vn-font-size-md)', color: 'var(--vn-fg-muted)' }}>
                  Each pool segregates workloads with distinct resource requirements · up to {totalMaxNodes} nodes total.
                </p>
                {d.pools.map((p, i) => {
                  const [, , cores, memory] = (p.flavor || '').split('-');
                  return (
                    <div key={i} style={{ marginTop: 'var(--vn-space-3)' }}>
                      <div style={{ fontWeight: 700, fontSize: 'var(--vn-font-size-md)' }}>
                        {d.uid}-{p.name} (Machine x {p.min})
                      </div>
                      <div style={{ fontSize: 'var(--vn-font-size-md)' }}>{p.flavor || '—'}</div>
                      {cores && memory && (
                        <div style={{ fontSize: 'var(--vn-font-size-md)', color: 'var(--vn-fg-muted)' }}>
                          {cores} vCPU — {memory} GiB RAM
                        </div>
                      )}
                      <div style={{ fontSize: 'var(--vn-font-size-md)', color: 'var(--vn-fg-muted)' }}>
                        {p.volumes} GiB High IOPS SSD
                      </div>
                    </div>
                  );
                })}
              </SumGroup>

              <SumGroup>
                <div style={{ fontWeight: 700, fontSize: 'var(--vn-font-size-md)' }}>
                  Cluster Add-ons{enabledAddonDefs.length ? ` (${enabledAddonDefs.length} enabled)` : ''}
                </div>
                {enabledAddonDefs.length === 0 ? (
                  <div style={{ fontSize: 'var(--vn-font-size-md)', color: 'var(--vn-fg-muted)' }}>No add-ons selected</div>
                ) : (
                  enabledAddonDefs.map((def) => (
                    <div key={def.key} style={{ fontSize: 'var(--vn-font-size-md)' }}>{def.label}</div>
                  ))
                )}
              </SumGroup>
            </CardBody>
            <CardFooter style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--vn-space-2)' }}>
              <Button variant="ghost" onClick={() => navigate(base)}>Cancel</Button>
              <Button
                startIcon={<Boxes size={15} />}
                disabled={!nameOk || !poolsOk || !subnetOk || overQuota || create.isPending}
                onClick={submit}
              >
                Create new Cluster
              </Button>
            </CardFooter>
          </Card>
        }
      >
        <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          {/* Cluster tier */}
          <Card>
            <CardHeader>
              <CardTitle>Cluster tier</CardTitle>
              <CardDescription>Choose how this cluster's control plane runs — both tiers live under the same Managed Kubernetes service.</CardDescription>
            </CardHeader>
            <CardBody>
              <div style={tierChoiceGrid}>
                {CREATE_TIERS.map((t) => {
                  const selected = tier === t.id;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      aria-pressed={selected}
                      disabled={t.disabled}
                      onClick={() => {
                        if (!t.disabled) setTier(t.id);
                      }}
                      style={{
                        ...tierChoiceCard,
                        ...(selected ? tierChoiceCardOn : null),
                        ...(t.disabled ? tierChoiceCardOff : null),
                      }}
                    >
                      <div style={tierHead}>
                        <span style={tierIcon}>{t.icon}</span>
                        <span style={tierTitle}>{t.title}</span>
                        {t.tag ? <span style={tierTag}>· {t.tag}</span> : null}
                        {t.disabled ? (
                          <Badge tone="neutral" appearance="subtle" style={{ marginLeft: 'auto' }}>
                            Coming soon
                          </Badge>
                        ) : t.beta ? (
                          <Badge tone="warning" appearance="subtle" style={{ marginLeft: 'auto' }}>
                            Beta
                          </Badge>
                        ) : null}
                      </div>
                      <p style={tierDesc}>{t.desc}</p>
                      <div style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
                        {t.points.map((p) => (
                          <span key={p.text} style={tierPoint}>
                            {p.yes ? (
                              <Check size={13} style={{ color: 'var(--vn-success-text)', flexShrink: 0 }} />
                            ) : (
                              <span style={tierDot} aria-hidden>
                                •
                              </span>
                            )}
                            {p.text}
                          </span>
                        ))}
                      </div>
                    </button>
                  );
                })}
              </div>
            </CardBody>
          </Card>

          {/* Cluster Information */}
          <Card>
            <CardHeader actions={refetchAction}>
              <CardTitle>Cluster Information</CardTitle>
              <CardDescription>Give your cluster a unique name. You won't be able to change this name after you create the cluster.</CardDescription>
            </CardHeader>
            <CardBody>
              <div style={{ display: 'grid', gap: 'var(--vn-space-3)', maxWidth: 480 }}>
                <Field
                  label="Cluster Name"
                  required
                  description="Letters, numbers, - and _. Must start with a letter or number, between 10 and 100 characters."
                  error={d.name.length > 0 && !nameOk ? 'Use 10–100 characters: letters, numbers, - or _, starting with a letter or number.' : undefined}
                >
                  {(p) => <Input {...p} value={d.name} onChange={(e) => patch({ name: e.target.value })} placeholder="production-cluster" invalid={d.name.length > 0 && !nameOk} />}
                </Field>
                <Field label="System ID">
                  {(p) => (
                    <>
                      <Input {...p} value={d.uid} disabled readOnly />
                      <span style={{ display: 'block', fontSize: 'var(--vn-font-size-xs, 11px)', color: 'var(--vn-fg-muted)', marginTop: 4 }}>
                        Auto-generated cluster identifier — managed by the system, cannot be changed.
                      </span>
                    </>
                  )}
                </Field>
                <Field label="Description">
                  {(p) => <Input {...p} value={d.description} onChange={(e) => patch({ description: e.target.value })} placeholder="Enter a description to help you remember this cluster." />}
                </Field>
              </div>
            </CardBody>
          </Card>

          {/* Cluster Configuration */}
          <Card>
            <CardHeader>
              <CardTitle>Cluster Configuration</CardTitle>
              <CardDescription>The following configuration options are needed for the cluster build.</CardDescription>
            </CardHeader>
            <CardBody>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(180px, 100%), 1fr))', gap: 'var(--vn-space-3)', maxWidth: 480 }}>
                <Field label="Kubernetes Version">
                  {(p) => (
                    <Select value={d.version} onValueChange={(v) => patch({ version: v })}>
                      <SelectTrigger {...p}><SelectValue /></SelectTrigger>
                      <SelectContent>{versions.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                </Field>
                <Field label="Container Runtime">
                  {(p) => (
                    <Select value={d.runtime} onValueChange={(v) => patch({ runtime: v })}>
                      <SelectTrigger {...p}><SelectValue /></SelectTrigger>
                      <SelectContent>{runtimes.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                </Field>
              </div>
            </CardBody>
            <CardFooter>
              <p style={{ margin: 0, fontSize: 'var(--vn-font-size-md)', color: 'var(--vn-fg-muted)' }}>
                Please select the Kubernetes version carefully, as only upgrades to newer versions are supported,
                downgrades are not possible.
              </p>
            </CardFooter>
          </Card>

          {/* Resource Group */}
          {resourceGroups.length > 0 && (
            <Card>
              <CardHeader actions={refetchAction}>
                <CardTitle>Resource Group</CardTitle>
                <CardDescription>To add new workload instances to a node pool, you must have a resource group in the same zone as your Kubernetes cluster.</CardDescription>
              </CardHeader>
              <CardBody>
                <div style={{ maxWidth: 480 }}>
                  <Select value={d.resource_group} onValueChange={(v) => patch({ resource_group: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {resourceGroups.map((r) => (
                        <SelectItem key={r.id ?? r.value} value={String(r.id ?? r.value)}>{r.name ?? r.label ?? r.value ?? r.id}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardBody>
            </Card>
          )}

          {/* Networking */}
          <Card>
            <CardHeader>
              <CardTitle>Networking</CardTitle>
              <CardDescription>Pod and service CIDRs must not overlap your VPC ranges.</CardDescription>
            </CardHeader>
            <CardBody>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(180px, 100%), 1fr))', gap: 'var(--vn-space-3)', maxWidth: 480 }}>
                <Field label="CNI Plugin">
                  {(p) => (
                    <Select value={d.network.cni} onValueChange={(v) => patch({ network: { ...d.network, cni: v } })}>
                      <SelectTrigger {...p}><SelectValue /></SelectTrigger>
                      <SelectContent>{cnis.map((c) => <SelectItem key={c.value} value={c.value} disabled={c.disabled}>{c.label}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                </Field>
                {networks.length > 0 ? (
                  <Field label="Subnet" required>
                    {(p) => (
                      <Select value={d.network.subnet} onValueChange={(v) => patch({ network: { ...d.network, subnet: v } })}>
                        <SelectTrigger {...p}><SelectValue placeholder="Select a network" /></SelectTrigger>
                        <SelectContent>
                          {networks.map((n) => {
                            const c = k8sNetworkCidr(n);
                            return <SelectItem key={n.id} value={n.id}>{n.name || n.id}{c ? ` (${c})` : ''}</SelectItem>;
                          })}
                        </SelectContent>
                      </Select>
                    )}
                  </Field>
                ) : <div />}
                <Field label="Node's CIDR">
                  {(p) => <Input {...p} value={d.network.pod_cidr} onChange={(e) => patch({ network: { ...d.network, pod_cidr: e.target.value } })} />}
                </Field>
                <Field label="Service's CIDR">
                  {(p) => <Input {...p} value={d.network.service_cidr} onChange={(e) => patch({ network: { ...d.network, service_cidr: e.target.value } })} />}
                </Field>
              </div>
            </CardBody>
          </Card>

          {/* Nodepool Configuration */}
          <Card>
            <CardHeader>
              <CardTitle>Nodepool Configuration</CardTitle>
              <CardDescription>Configure the resources of your node pool (e.g., CPU, memory) to suit your workload.</CardDescription>
            </CardHeader>
            <CardBody>
              <PoolsStep pools={d.pools} clusterUid={d.uid} flavors={flavors} gpuModels={gpuModels} onChange={(pools) => patch({ pools })} />
            </CardBody>
            <CardFooter>
              <p style={{ margin: 0, fontSize: 'var(--vn-font-size-md)', color: 'var(--vn-fg-muted)' }}>
                The maximum number of nodes you set helps the system automatically adjust the size of your node pool. If
                your workload needs more resources, the system will automatically add nodes up to the maximum you've
                allowed.
              </p>
            </CardFooter>
          </Card>

          {/* Add-ons */}
          <Card>
            <CardHeader>
              <CardTitle>Add-ons Configuration</CardTitle>
              <CardDescription>Enable and configure optional Kubernetes add-ons to enhance your cluster functionality.</CardDescription>
            </CardHeader>
            <CardBody>
              <AddonsStep addons={d.addons} flavors={flavors} onChange={(addons) => patch({ addons })} />
            </CardBody>
          </Card>

          {/* Tags */}
          <Card>
            <CardHeader>
              <CardTitle>Tags</CardTitle>
              <CardDescription>Tags are labels you assign to resources, consisting of a key-value pair.</CardDescription>
            </CardHeader>
            <CardBody>
              <TagsStep tags={d.tags} onChange={(tags) => patch({ tags })} />
            </CardBody>
          </Card>
        </div>
      </SplitView>
    </Container>
  );
}

/** Summary block separated from the previous block by a top divider. */
function SumGroup({ first, children }: { first?: boolean; children: React.ReactNode }) {
  return (
    <div
      style={{
        paddingTop: first ? 0 : 'var(--vn-space-4)',
        marginTop: first ? 0 : 'var(--vn-space-4)',
        borderTop: first ? 'none' : '1px solid var(--vn-border-subtle, var(--vn-border-default))',
        display: 'grid',
        gap: 'var(--vn-space-3)',
      }}
    >
      {children}
    </div>
  );
}

/** Label (muted bold) over a value, used throughout the summary rail. */
function SumField({ label, value }: { label: React.ReactNode; value: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 'var(--vn-font-size-md)', fontWeight: 700, color: 'var(--vn-fg-muted)' }}>{label}</div>
      <div style={{ fontSize: 'var(--vn-font-size-md)' }}>{value}</div>
    </div>
  );
}

// ── Cluster tier picker (chosen per cluster under the one Managed Kubernetes service) ──
type ClusterTier = 'enterprise' | 'developer';

interface CreateTierPoint {
  text: string;
  yes: boolean;
}
interface CreateTier {
  id: ClusterTier;
  title: string;
  tag?: string;
  beta?: boolean;
  /** Temporarily not selectable (provisioning not shipped yet) → shows "Coming soon". */
  disabled?: boolean;
  icon: ReactNode;
  desc: string;
  points: CreateTierPoint[];
}

const CREATE_TIERS: CreateTier[] = [
  {
    id: 'enterprise',
    title: 'Enterprise',
    tag: 'Standard',
    icon: <ShieldCheck size={16} />,
    desc: 'Production-grade, highly available.',
    points: [
      { text: 'HA control plane (3 masters)', yes: true },
      { text: '99.9% SLA', yes: true },
      { text: 'Full add-ons & ingress', yes: true },
    ],
  },
  {
    id: 'developer',
    title: 'Developer',
    beta: true,
    disabled: true,
    icon: <Zap size={16} />,
    desc: 'Fast, low-cost — for dev & experiments.',
    points: [
      { text: 'Hosted control plane (we run it)', yes: true },
      { text: 'Single CP · No HA · No SLA', yes: false },
      { text: 'Same add-ons · spins up faster', yes: true },
    ],
  },
];

const tierChoiceGrid: CSSProperties = {
  display: 'grid',
  // At mobile widths each tier card needs at least ~220px to be readable; below
  // that the two-column layout collapses to a single column automatically.
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(220px, 100%), 1fr))',
  gap: 'var(--vn-space-3)',
};
const tierChoiceCard: CSSProperties = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  cursor: 'pointer',
  font: 'inherit',
  color: 'var(--vn-fg-default)',
  border: '1px solid var(--vn-border-default)',
  borderRadius: 'var(--vn-radius-lg)',
  background: 'var(--vn-bg-surface)',
  padding: 'var(--vn-space-4)',
};
const tierChoiceCardOn: CSSProperties = {
  borderColor: 'var(--vn-primary-solid)',
  background: 'var(--vn-primary-subtle)',
  boxShadow: '0 0 0 1px var(--vn-primary-solid)',
};
const tierChoiceCardOff: CSSProperties = {
  opacity: 0.6,
  cursor: 'not-allowed',
};
const tierHead: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--vn-space-2)',
  marginBottom: 'var(--vn-space-1)',
};
const tierIcon: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 26,
  height: 26,
  borderRadius: 'var(--vn-radius-md)',
  background: 'var(--vn-bg-muted)',
  color: 'var(--vn-fg-default)',
  flexShrink: 0,
};
const tierTitle: CSSProperties = { fontWeight: 600, fontSize: 'var(--vn-font-size-sm)' };
const tierTag: CSSProperties = { color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' };
const tierDesc: CSSProperties = {
  color: 'var(--vn-fg-muted)',
  fontSize: 'var(--vn-font-size-sm)',
  margin: '0 0 var(--vn-space-2)',
};
const tierPoint: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--vn-space-2)',
  fontSize: 'var(--vn-font-size-sm)',
  color: 'var(--vn-fg-default)',
};
const tierDot: CSSProperties = {
  width: 13,
  textAlign: 'center',
  color: 'var(--vn-fg-subtle)',
  flexShrink: 0,
};

